import { yupResolver } from "@hookform/resolvers/yup";
import { Box } from "@material-ui/core";
import { Grid } from "@material-ui/core";
import useIsMounted from "ismounted";
import { useContext, useEffect, useMemo } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { reach } from "yup";
import { Country } from "../../../../../../../components/forms/country";
import { KYBJurisdiction } from "../../../../../../../components/forms/kyb-jurisdictions";
import { NumberField } from "../../../../../../../components/forms/number";
import { TextField } from "../../../../../../../components/forms/text";
import { StepperContext } from "../../../../../../../components/stepper/context";
import { VerificationDTO } from "../../../../../../../store/verification/models/dto/due-diligence";
import { Container } from "../../../common/components";
import { ConfigContext } from "../../../common/config-context";
import { Attachments } from "../../../common/pages/common/attachment";
import { AttachmentContext } from "../../attachment-context";
import { FormState } from "../../form-context";
import { Wrapper } from "../common/components";
import { useFileUploader } from "./use-file-uploader";
import { getCountries } from 'libphonenumber-js';
import isoCountries from 'i18n-iso-countries';
import countryDataEn from 'i18n-iso-countries/langs/en.json';


isoCountries.registerLocale(countryDataEn);

export const getCountryName = (code: string) => {
  let name = isoCountries.getName(code as string, 'en');

  if (!name) {
    if (code === 'AC') {
      name = 'Ascension';
    } else if (code === 'TA') {
      name = 'Tristan da Cunha';
    }
  }

  return name || '';
};


const Files = styled.div`
  grid-column-start: 1;
  grid-column-end: 3;
`;

type FormType = VerificationDTO.Requests.DueDiligence.Form.Post.Final.T["companyDetails"];

export function CompanyDetails() {
  const { t } = useTranslation("fields");
  const isMounted = useIsMounted();

  const {
    state: { contactEmail, dueDiligenceId },
    item: { enabledOptionalFields },
  } = useContext(ConfigContext);
  const { setData, data } = useContext(FormState);

  const { setValidation, setIsBackEnabled } = useContext(StepperContext);

  const {
    validation,
  } = VerificationDTO.Requests.DueDiligence.Form.Post.Final.useValidations(
    dueDiligenceId,
    enabledOptionalFields.includes("TOKENOMICS"),
    enabledOptionalFields.includes("WHITEPAPER")
  );

  const form = useForm<FormType>({
    resolver: yupResolver(
      useMemo(() => reach(validation, "companyDetails").required(), [
        validation,
      ])
    ),
    defaultValues: data.companyDetails,
    delayError: 300,
  });

  const { control, handleSubmit } = form;
  const { company } = useContext(AttachmentContext);

  const [chartFileSubject, chartUploaded, chartFileSuccess] = useFileUploader(
    "COMPANY_STRUCTURE_CHART"
  );

  useEffect(() => {
    const sub = chartFileSuccess.subscribe((d) => {
      company.setFile("COMPANY_STRUCTURE_CHART", d);
    });
    return () => sub.unsubscribe();
  }, [chartFileSuccess, company.setFile]);

  const chartError = useMemo(
    () =>
      !chartUploaded &&
        form.formState.isSubmitted &&
        !company.files.COMPANY_STRUCTURE_CHART
        ? new Error("Field is required")
        : undefined,
    [
      chartUploaded,
      form.formState.isSubmitted,
      company.files.COMPANY_STRUCTURE_CHART,
    ]
  );

  const [
    incubationFileSubject,
    incubationUploaded,
    incubationFileSuccess,
  ] = useFileUploader("INCUBATION_OR_INCORPORATION");

  useEffect(() => {
    const sub = incubationFileSuccess.subscribe((d) => {
      company.setFile("INCUBATION_OR_INCORPORATION", d);
    });
    return () => sub.unsubscribe();
  }, [incubationFileSuccess, company.setFile]);

  const incubationError = useMemo(
    () =>
      !incubationUploaded && form.formState.isSubmitted
        ? new Error("Field is required")
        : undefined,
    [incubationUploaded, form.formState.isSubmitted]
  );

  /** Set up the validation for the form */
  useEffect(() => {
    setValidation(() => () =>
      new Promise<void>((res, rej) => {
        if (
          (!chartUploaded || !incubationUploaded) &&
          (!company.files.INCUBATION_OR_INCORPORATION ||
            !company.files.COMPANY_STRUCTURE_CHART)
        )
          rej();
        handleSubmit((data: FormType) => {
          setData({
            companyDetails: data,
          });
          res();
        })();
      })
    );

    return () => {
      if (isMounted.current) {
        // We have to check if it's mounted otherwise it will clear current validation
        // This is because the prior page's clear validation is run after the new page's setValidation
        setValidation(() => () => Promise.resolve());
      }
    };
  }, [
    chartUploaded,
    incubationUploaded,
    setValidation,
    handleSubmit,
    isMounted.current,
    company.files.INCUBATION_OR_INCORPORATION,
    company.files.COMPANY_STRUCTURE_CHART,
  ]);

  useEffect(() => {
    // User can never go back to init
    setIsBackEnabled(false);
  }, [setIsBackEnabled]);

  return (
    <Box component='div' sx={{ bgcolor: 'rgb(240, 248, 255)', borderRadius: '5px', padding: '1.4em' }}>

      <Container>

        <Wrapper>
          <FormProvider {...form}>

            {t(`company-registration-number`)}
            <Controller
              control={control}
              name="companyRegistrationNumber"
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  error={!!error}
                  helperText={error?.message}
                  type="text"
                  label=""
                />
              )}
            />
            {t(`tax-identification-no`)}
            <Controller
              control={control}
              name="taxIdentificationNo"
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  error={!!error}
                  helperText={error?.message}
                  type="text"
                  label=""
                />
              )}
            />
            {t(`tax-country`)}
            <Controller
              control={control}
              name="taxCountry"
              render={({ field, fieldState: { error } }) => (
                <Country {...field} error={!!error} helperText={error?.message} />
              )}
            />
            {t(`company-jurisdiction-country-code`)}
            <Controller
              control={control}
              name="companyJurisdictionCountryCode"
              render={({ field, fieldState: { error } }) => (
                <KYBJurisdiction
                  fullWidth
                  jurisdictions={
                    getCountries().map((code) => ({ code: code, name: getCountryName(code) }))
                  }
                  {...field}
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
            {t(`estimated-annual-turnover`)}
            <Controller
              control={control}
              name="estimatedAnnualTurnover"
              render={({ field, fieldState: { error } }) => (
                <NumberField
                  {...field}
                  error={!!error}
                  helperText={error?.message}
                  type="text"
                  label=""
                />
              )}
            />
            {enabledOptionalFields.includes("WHITEPAPER") ? (
              <>
                {t(`whitepaper-url`)}
                <Controller
                  control={control}
                  name="whitepaperUrl"
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      error={!!error}
                      helperText={error?.message}
                      type="text"
                      label=""
                    />
                  )}
                />
              </>
            ) : null}
            {enabledOptionalFields.includes("TOKENOMICS") ? (
              <>
                {" "}
                {t(`tokenomics-url`)}
                <Controller
                  control={control}
                  name="tokenomicsUrl"
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      error={!!error}
                      helperText={error?.message}
                      type="text"
                      label=""
                    />
                  )}
                />
              </>
            ) : null}
            <Files>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Attachments
                    category="COMPANY_STRUCTURE_CHART"
                    disabled={!contactEmail}
                    valueSubject={chartFileSubject}
                    error={chartError}
                    initialValue={company.files.COMPANY_STRUCTURE_CHART}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Attachments
                    category="INCUBATION_OR_INCORPORATION"
                    disabled={!contactEmail}
                    valueSubject={incubationFileSubject}
                    error={incubationError}
                    initialValue={company.files.INCUBATION_OR_INCORPORATION}
                  />
                </Grid>
              </Grid>
            </Files>
          </FormProvider>
        </Wrapper>

      </Container>
    </Box>

  );
}
